import React, { useEffect, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Check, X, MapPin, Calendar, Globe, Instagram, Briefcase, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};

function StatusPill({ status, t }) {
  return (
    <span data-testid={`pr-status-${status}`} className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATUS_STYLE[status] || STATUS_STYLE.pending}`}>
      {t(`pr_status_${status}`)}
    </span>
  );
}

function RequestCard({ r, onAccept, onRefuse }) {
  const { t, lang } = useI18n();
  const loc = lang === "fr" ? "fr-FR" : "en-US";
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(loc, { day: "numeric", month: "short", year: "numeric" }) : "—");
  const services = r.services_offered?.length ? r.services_offered : r.services_listed || [];
  const initials = (r.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <article data-testid={`pr-card-${r.id}`} className="bg-white border border-stone-200 rounded-3xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_28px_rgba(0,0,0,0.06)] transition-shadow duration-300">
      <div className="p-6 flex flex-col sm:flex-row gap-5">
        <div className="shrink-0">
          {r.avatar ? (
            <img src={r.avatar} alt={r.name} className="w-20 h-20 rounded-2xl object-cover border border-stone-100" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-stone-900 text-stone-50 font-serif text-2xl flex items-center justify-center">{initials}</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 data-testid="pr-name" className="font-serif text-2xl leading-tight">{r.name}</h3>
              <div className="text-xs text-stone-500 mt-0.5">{r.email}</div>
            </div>
            <StatusPill status={r.status} t={t} />
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm text-stone-600">
            <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-stone-400" />{r.location || t("pr_no_location")}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={14} className="text-stone-400" />{t("pr_requested_on")} {fmt(r.requested_at)}</span>
            {r.years_experience > 0 && <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-stone-400" />{r.years_experience} {t("pr_experience")}</span>}
            {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 underline decoration-stone-300 hover:decoration-stone-900"><Globe size={14} className="text-stone-400" />{t("ps_website")}</a>}
            {r.instagram && <span className="inline-flex items-center gap-1.5"><Instagram size={14} className="text-stone-400" />{r.instagram}</span>}
          </div>

          <div className="mt-4">
            <div className="eyebrow text-stone-500 text-[11px] flex items-center gap-1.5"><Briefcase size={12} />{t("pr_services")}</div>
            {services.length ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {services.map((s, i) => <span key={i} className="text-xs px-3 py-1 rounded-full bg-stone-100 text-stone-700">{s}</span>)}
              </div>
            ) : <div className="text-sm text-stone-400 mt-1">—</div>}
          </div>

          <div className="mt-4">
            <div className="eyebrow text-stone-500 text-[11px]">{t("pr_description")}</div>
            <p className="text-sm text-stone-700 mt-1.5 leading-relaxed whitespace-pre-line">{r.bio || <span className="text-stone-400">{t("pr_no_bio")}</span>}</p>
          </div>

          {r.status === "rejected" && r.rejection_reason && (
            <div className="mt-4 text-sm bg-red-50/60 border border-red-100 rounded-xl p-3 text-red-900"><span className="font-medium">{t("pr_reason")} :</span> {r.rejection_reason}</div>
          )}
          {r.reviewed_at && r.status !== "pending" && (
            <div className="text-xs text-stone-400 mt-3">{t("pr_reviewed_on")} {fmt(r.reviewed_at)}</div>
          )}
        </div>
      </div>

      {r.status !== "approved" && (
        <div className="px-6 py-4 bg-stone-50 border-t border-stone-100 flex flex-col sm:flex-row gap-3 sm:justify-end">
          {r.status === "pending" && (
            <Button data-testid={`pr-refuse-${r.id}`} variant="outline" onClick={() => onRefuse(r)} className="rounded-full h-11 px-6 border-stone-300 text-stone-800 hover:bg-red-50 hover:text-red-800 hover:border-red-200">
              <X size={16} className="mr-2" />{t("pr_refuse")}
            </Button>
          )}
          <Button data-testid={`pr-accept-${r.id}`} onClick={() => onAccept(r)} className="rounded-full h-11 px-7 bg-stone-900 hover:bg-stone-800 text-stone-50">
            <Check size={16} className="mr-2" />{t("pr_accept")}
          </Button>
        </div>
      )}
    </article>
  );
}

export function ProviderRequests({ onChanged }) {
  const { t } = useI18n();
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [list, all] = await Promise.all([
      api.get(`/admin/provider-requests${filter === "all" ? "" : `?status=${filter}`}`).then((r) => r.data),
      api.get("/admin/provider-requests").then((r) => r.data),
    ]);
    setItems(list);
    setCounts({
      pending: all.filter((x) => x.status === "pending").length,
      approved: all.filter((x) => x.status === "approved").length,
      rejected: all.filter((x) => x.status === "rejected").length,
      all: all.length,
    });
  };
  useEffect(() => { load(); }, [filter]);

  const act = async () => {
    setBusy(true);
    try {
      if (confirm.type === "accept") {
        await api.post(`/admin/provider-requests/${confirm.r.id}/accept`);
        toast.success(t("pr_accepted_toast"));
      } else {
        await api.post(`/admin/provider-requests/${confirm.r.id}/refuse`, { reason });
        toast.success(t("pr_refused_toast"));
      }
      setConfirm(null); setReason("");
      await load(); onChanged?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const filters = [["pending", t("pr_pending")], ["approved", t("pr_accepted")], ["rejected", t("pr_refused")], ["all", t("pr_all")]];

  return (
    <div className="mt-6" data-testid="provider-requests">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl">{t("pr_title")}</h2>
          <p className="text-stone-600 text-sm mt-1">{t("pr_subtitle")}</p>
        </div>
        <div className="inline-flex flex-wrap gap-1 bg-stone-100 rounded-full p-1">
          {filters.map(([k, l]) => (
            <button key={k} data-testid={`pr-filter-${k}`} onClick={() => setFilter(k)} className={`px-4 h-9 rounded-full text-sm transition-colors ${filter === k ? "bg-white shadow-sm font-medium text-stone-900" : "text-stone-600 hover:text-stone-900"}`}>
              {l}{counts[k] != null && <span className={`ml-2 text-xs ${filter === k ? "text-stone-500" : "text-stone-400"}`}>{counts[k]}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid gap-5">
        {items === null && <div className="text-sm text-stone-500 p-8">{t("loading")}</div>}
        {items?.length === 0 && (
          <div data-testid="pr-empty" className="bg-white border border-dashed border-stone-300 rounded-3xl p-14 text-center text-stone-500 text-sm">{t("pr_none")}</div>
        )}
        {items?.map((r) => (
          <RequestCard key={r.id} r={r} onAccept={(x) => setConfirm({ type: "accept", r: x })} onRefuse={(x) => setConfirm({ type: "refuse", r: x })} />
        ))}
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) { setConfirm(null); setReason(""); } }}>
        <DialogContent className="bg-white max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{confirm?.type === "accept" ? t("pr_confirm_accept_t") : t("pr_confirm_refuse_t")}</DialogTitle>
            <DialogDescription className="text-stone-600">
              {(confirm?.type === "accept" ? t("pr_confirm_accept_d") : t("pr_confirm_refuse_d")).replace("{name}", confirm?.r?.name || "")}
            </DialogDescription>
          </DialogHeader>
          {confirm?.type === "refuse" && (
            <div>
              <Label>{t("pr_reason_label")}</Label>
              <Textarea data-testid="pr-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("pr_reason_ph")} rows={3} className="mt-2" />
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button data-testid="pr-cancel" variant="outline" className="rounded-full" onClick={() => { setConfirm(null); setReason(""); }}>{t("cancel")}</Button>
            <Button data-testid="pr-confirm" disabled={busy} onClick={act} className={`rounded-full text-stone-50 ${confirm?.type === "refuse" ? "bg-red-700 hover:bg-red-800" : "bg-stone-900 hover:bg-stone-800"}`}>
              {busy ? "..." : confirm?.type === "accept" ? t("pr_accept") : t("pr_refuse")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
